import { stampToDate, getLocalisedDate, getLocalisedTime, secondsToHms, isSameDay } from './datetime';
import { DATE_RANGES } from './constants';

// xlsx-js-style is loaded lazily — only downloaded when user clicks Download.
let _XLSX = null;
const getXLSX = async () => {
  if (!_XLSX) _XLSX = (await import('xlsx-js-style')).default;
  return _XLSX;
};

// ─── Colours ──────────────────────────────────────────────────────────────────
const C = {
  NAVY:        'FF1B3A6B',
  MED_BLUE:    'FF2E5FA3',
  ROW_BLUE:    'FFD6E4F7',
  INFO_BG:     'FFEBF3FB',
  WHITE:       'FFFFFFFF',
  GREEN_FILL:  'FFC8F5C8',
  GREEN_TEXT:  'FF1A6B2E',
  RED_FILL:    'FFFFB3B3',
  RED_TEXT:    'FFC0392B',
  LINK_TEXT:   'FF1155CC',
  BODY_TEXT:   'FF1A1A2E',
  WHITE_TEXT:  'FFFFFFFF',
};

const BORDER = {
  top:    { style: 'thin', color: { rgb: 'FFD0DCF0' } },
  bottom: { style: 'thin', color: { rgb: 'FFD0DCF0' } },
  left:   { style: 'thin', color: { rgb: 'FFD0DCF0' } },
  right:  { style: 'thin', color: { rgb: 'FFD0DCF0' } },
};

// wrapText is true on every cell in the reference
const st = (bg, fc, bold, sz, ha, borders) => ({
  ...(bg ? { fill: { patternType: 'solid', fgColor: { rgb: bg } } } : {}),
  font: { name: 'Arial', sz: sz || 9, bold: !!bold, color: { rgb: fc || C.BODY_TEXT } },
  alignment: { horizontal: ha || 'center', vertical: 'center', wrapText: true },
  ...(borders !== false ? { border: BORDER } : {}),
});

const sc = (XLSX, ws, col, row, val, style, link) => {
  const ref = XLSX.utils.encode_cell({ r: row - 1, c: col - 1 });
  ws[ref] = { v: val == null ? '' : val, t: typeof val === 'number' ? 'n' : 's', s: style };
  if (link) ws[ref].l = { Target: link };
};

const addMerge = (arr, c1, r1, c2, r2) =>
  arr.push({ s: { r: r1 - 1, c: c1 - 1 }, e: { r: r2 - 1, c: c2 - 1 } });

// ─── Sheet: Summary ───────────────────────────────────────────────────────────
// Exact layout from reference:
// R1  Title (A1:K1 merged)
// R2  Date + Generated On (A2:K2 merged)
// R3  Section label (A3:K3 merged)
// R4  Column headers
// R5…  Vehicle data rows (alternating D6E4F7 / FFFFFF)
// Last  GRAND TOTAL row
function buildSummary(XLSX, wb, data) {
  const ws = {};
  const merges = [];
  const { dateRange, generatedOn, vehicles = [] } = data;

  ws['!cols'] = [
    { wch: 5  }, // A  Sr.
    { wch: 12 }, // B  Vehicle Number
    { wch: 17 }, // C  Vehicle Type
    { wch: 10 }, // D  Total Routes
    { wch: 9  }, // E  Total Trips
    { wch: 10 }, // F  Total Drivers
    { wch: 14 }, // G  Actual Route KMS
    { wch: 13 }, // H  Est. Route KMS
    { wch: 14 }, // I  KMS Deviation
    { wch: 12 }, // J  Avg KMS / Trip
    { wch: 14 }, // K  Deviation %
  ];

  ws['!rows'] = [
    { hpt: 25.5  },                              // R1 title
    { hpt: 21.75 },                              // R2 date info
    { hpt: 18    },                              // R3 section label
    { hpt: 36    },                              // R4 column headers
    ...vehicles.map(() => ({ hpt: 19.5 })),      // data rows
    { hpt: 21.75 },                              // grand total
  ];

  // R1 – Title
  addMerge(merges, 1, 1, 11, 1);
  sc(XLSX, ws, 1, 1, '🚌  AJJAS - VEHICLE TRACKING REPORT',
    st(C.NAVY, C.WHITE_TEXT, true, 14, 'center', false));

  // R2 – Date info
  addMerge(merges, 1, 2, 11, 2);
  sc(XLSX, ws, 1, 2,
    `Date:  ${dateRange}  |  Generated On:  ${generatedOn}`,
    st(C.INFO_BG, null, false, 9, 'left', false));

  // R3 – Section label
  addMerge(merges, 1, 3, 11, 3);
  sc(XLSX, ws, 1, 3,
    '  VEHICLE SUMMARY   (Click a Vehicle Number to drill down into trip details)',
    st(C.MED_BLUE, C.WHITE_TEXT, true, 9, 'left', false));

  // R4 – Column headers (11 columns)
  const hSt = st(C.NAVY, C.WHITE_TEXT, true, 10, 'center');
  ['Sr.', 'Vehicle\nNumber', 'Vehicle\nType', 'Total\nRoutes', 'Total\nTrips',
    'Total\nDrivers', 'Actual\nRoute KMS', 'Est. Route\nKMS',
    'KMS\nDeviation', 'Avg KMS\n/ Trip', 'Deviation %']
    .forEach((h, i) => sc(XLSX, ws, i + 1, 4, h, hSt));

  // Vehicle data rows starting at R5
  vehicles.forEach((veh, idx) => {
    const row    = 5 + idx;
    const rowBg  = idx % 2 === 0 ? C.ROW_BLUE : C.WHITE;
    const dSt    = (ha) => st(rowBg, C.BODY_TEXT, false, 9, ha);
    const dev    = parseFloat((veh.actualKms - veh.estKms).toFixed(1));
    const pct    = veh.estKms ? (dev / veh.estKms) * 100 : 0;
    const pctStr = (pct > 0 ? '+' : '') + pct.toFixed(1) + '%';
    const avg    = parseFloat((veh.actualKms / Math.max(veh.totalTrips, 1)).toFixed(1));
    const devSt  = dev < 0
      ? st(C.GREEN_FILL, C.GREEN_TEXT, false, 9, 'right')
      : dev > 0
        ? st(C.RED_FILL,   C.RED_TEXT,   true,  9, 'right')
        : dSt('right');

    sc(XLSX, ws, 1,  row, idx + 1,          dSt('center'));
    sc(XLSX, ws, 2,  row, veh.id,
      st(rowBg, C.LINK_TEXT, true, 9, 'center'),
      `#'${veh.id}'!A1`);
    sc(XLSX, ws, 3,  row, veh.type,         dSt('left'));
    sc(XLSX, ws, 4,  row, veh.totalRoutes,  dSt('center'));
    sc(XLSX, ws, 5,  row, veh.totalTrips,   dSt('center'));
    sc(XLSX, ws, 6,  row, veh.totalDrivers, dSt('center'));
    sc(XLSX, ws, 7,  row, veh.actualKms,    dSt('right'));
    sc(XLSX, ws, 8,  row, veh.estKms,       dSt('right'));
    sc(XLSX, ws, 9,  row, dev  !== 0 ? dev    : '', devSt);
    sc(XLSX, ws, 10, row, avg,                       dSt('right'));
    sc(XLSX, ws, 11, row, pct !== 0 ? pctStr : '', dSt('right'));
  });

  const totalRow   = 5 + vehicles.length;
  const tSt        = (ha) => st(C.NAVY, C.WHITE_TEXT, true, 9, ha);
  const totRoutes  = vehicles.reduce((s, v) => s + v.totalRoutes,  0);
  const totTrips   = vehicles.reduce((s, v) => s + v.totalTrips,   0);
  const totDrivers = vehicles.reduce((s, v) => s + v.totalDrivers, 0);
  const totActual  = parseFloat(vehicles.reduce((s, v) => s + v.actualKms, 0).toFixed(1));
  const totEst     = parseFloat(vehicles.reduce((s, v) => s + v.estKms,    0).toFixed(1));

  sc(XLSX, ws, 1,  totalRow, '',            tSt('center'));
  sc(XLSX, ws, 2,  totalRow, 'GRAND TOTAL', tSt('center'));
  sc(XLSX, ws, 3,  totalRow, '',            tSt('center'));
  sc(XLSX, ws, 4,  totalRow, totRoutes,     tSt('right'));
  sc(XLSX, ws, 5,  totalRow, totTrips,      tSt('right'));
  sc(XLSX, ws, 6,  totalRow, totDrivers,    tSt('right'));
  sc(XLSX, ws, 7,  totalRow, totActual,     tSt('right'));
  sc(XLSX, ws, 8,  totalRow, totEst,        tSt('right'));
  sc(XLSX, ws, 9,  totalRow, '',            tSt('center'));  // I – KMS Deviation (blank in total)
  sc(XLSX, ws, 10, totalRow, '',            tSt('center'));  // J – Avg KMS (blank in total)
  sc(XLSX, ws, 11, totalRow, '',            tSt('center'));  // K – Deviation % (blank in total)


  ws['!merges'] = merges;
  ws['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: totalRow - 1, c: 10 } });
  XLSX.utils.book_append_sheet(wb, ws, 'Summary');
}

// ─── Sheet: Individual vehicle ────────────────────────────────────────────────
// Exact layout from reference:
// R1  Title (A1:I1 merged)
// R2  Date + Vehicle + Type + Trips (A2:I2 merged)
// R3  KMS summary (A3:I3 merged)
// R4  Empty spacer (A4:I4 merged, no value)
// R5  Back to Summary link (A5:I5 merged)
// R6  Column headers
// R7…  Trip data rows
// Last  TOTAL row
function buildVehicleSheet(XLSX, wb, veh) {
  const ws = {};
  const merges = [];
  const { id, type, actualKms, estKms, dateRange, trips = [] } = veh;

  ws['!cols'] = [
    { wch: 18.86 }, // A  Date
    { wch: 20    }, // B  Driver Name
    { wch: 42    }, // C  Route Name
    { wch: 13    }, // D  Start Time
    { wch: 12    }, // E  End Time
    { wch: 12    }, // F  Duration
    { wch: 14    }, // G  Actual KMS
    { wch: 13    }, // H  Est. KMS
    { wch: 14    }, // I  KMS Deviation
    { wch: 14    }, // J  Deviation %
    { wch: 11    }, // K  KMS Source
  ];

  ws['!rows'] = [
    { hpt: 25.5  }, // R1 title
    { hpt: 21.75 }, // R2 vehicle info
    { hpt: 18    }, // R3 KMS info
    { hpt: 13.5  }, // R4 spacer
    { hpt: 18    }, // R5 back button
    { hpt: 36    }, // R6 column headers
    ...trips.map(() => ({ hpt: 19.5 })),
    { hpt: 21.75 },  // total row
  ];

  const devKms    = parseFloat((actualKms - estKms).toFixed(1));
  const devPct    = estKms ? (devKms / estKms) * 100 : 0;
  const devKmsStr = devKms !== 0 ? (devKms > 0 ? `+${devKms}` : `${devKms}`) : '0';
  const devPctStr = (devPct > 0 ? '+' : '') + devPct.toFixed(1) + '%';

  // R1 – Title (merges span all 10 columns A–J)
  addMerge(merges, 1, 1, 11, 1);
  sc(XLSX, ws, 1, 1, '🚌  AJJAS - VEHICLE TRACKING REPORT',
    st(C.NAVY, C.WHITE_TEXT, true, 14, 'center', false));

  // R2 – Vehicle info
  addMerge(merges, 1, 2, 11, 2);
  sc(XLSX, ws, 1, 2,
    `Date:  ${dateRange || ''}  |  Vehicle:  ${id}  |  Type:  ${type}  |  Total Trips:  ${trips.length}`,
    st(C.INFO_BG, null, false, 9, 'left', false));

  // R3 – KMS summary — shows both absolute deviation and %
  addMerge(merges, 1, 3, 11, 3);
  sc(XLSX, ws, 1, 3,
    `Actual Route KMS:  ${actualKms}  |  Est. Route KMS:  ${estKms}  |  KMS Deviation:  ${devKmsStr} km  (${devPctStr})`,
    st(C.INFO_BG, null, false, 9, 'left', false));

  // R4 – Empty spacer
  addMerge(merges, 1, 4, 11, 4);
  sc(XLSX, ws, 1, 4, '', st(C.INFO_BG, null, false, 9, 'left', false));

  // R5 – Back to Summary
  addMerge(merges, 1, 5, 11, 5);
  sc(XLSX, ws, 1, 5, '  < Back to Summary',
    st(C.ROW_BLUE, C.LINK_TEXT, true, 9, 'left'),
    '#Summary!A1');

  // R6 – Column headers (10 columns)
  const hSt = st(C.NAVY, C.WHITE_TEXT, true, 10, 'center');
  ['Date', 'Driver Name', 'Route Name', 'Trip Start Time', 'Trip End Time',
    'Trip Duration', 'Actual Route KMS', 'Est. Route KMS', 'KMS\nDeviation', 'Deviation %', 'KMS\nSource']
    .forEach((h, i) => sc(XLSX, ws, i + 1, 6, h, hSt));

  // Trip rows starting at R7
  trips.forEach((trip, idx) => {
    const row        = 7 + idx;
    const rowBg      = idx % 2 === 0 ? C.ROW_BLUE : C.WHITE;
    const dSt        = (ha) => st(rowBg, C.BODY_TEXT, false, 9, ha);
    const tripDevKms = parseFloat((trip.actualKms - trip.estKms).toFixed(1));
    const tripDevPct = trip.estKms ? (tripDevKms / trip.estKms) * 100 : 0;
    const tripDevKmsStr = tripDevKms !== 0 ? (tripDevKms > 0 ? `+${tripDevKms}` : `${tripDevKms}`) : '';
    const tripDevPctStr = tripDevPct !== 0 ? (tripDevPct > 0 ? '+' : '') + tripDevPct.toFixed(1) + '%' : '';
    const tripDevSt  = tripDevKms < 0
      ? st(C.GREEN_FILL, C.GREEN_TEXT, false, 9, 'right')
      : tripDevKms > 0
        ? st(C.RED_FILL, C.RED_TEXT, false, 9, 'right')
        : dSt('right');

    sc(XLSX, ws, 1, row, trip.date,       dSt('center'));
    sc(XLSX, ws, 2, row, trip.driverName, dSt('left'));
    sc(XLSX, ws, 3, row, trip.routeName,  dSt('left'));
    sc(XLSX, ws, 4, row, trip.startTime,  dSt('center'));
    sc(XLSX, ws, 5, row, trip.endTime,    dSt('center'));
    sc(XLSX, ws, 6, row, trip.duration,   dSt('center'));
    sc(XLSX, ws, 7, row, trip.actualKms,  dSt('right'));
    sc(XLSX, ws, 8, row, trip.estKms,     dSt('right'));
    sc(XLSX, ws, 9,  row, tripDevKmsStr,   tripDevSt);
    sc(XLSX, ws, 10, row, tripDevPctStr,   tripDevSt);
    sc(XLSX, ws, 11, row, trip.kmsSource,  dSt('center'));
  });

  // TOTAL row — all 10 cols get navy fill
  const totalRow  = 7 + trips.length;
  const tSt       = (ha) => st(C.NAVY, C.WHITE_TEXT, true, 9, ha);
  sc(XLSX, ws, 1, totalRow, 'TOTAL', tSt('center'));
  sc(XLSX, ws, 2, totalRow, '', tSt('center'));   // B – navy, empty
  sc(XLSX, ws, 3, totalRow, '', tSt('center'));   // C – navy, empty
  sc(XLSX, ws, 4, totalRow, '', tSt('center'));   // D – navy, empty
  sc(XLSX, ws, 5, totalRow, '', tSt('center'));   // E – navy, empty
  sc(XLSX, ws, 6, totalRow, '', tSt('center'));   // F – navy, empty
  sc(XLSX, ws, 7, totalRow,
    parseFloat(trips.reduce((s, t) => s + t.actualKms, 0).toFixed(1)), tSt('right'));
  sc(XLSX, ws, 8, totalRow,
    parseFloat(trips.reduce((s, t) => s + t.estKms,    0).toFixed(1)), tSt('right'));
  sc(XLSX, ws, 9,  totalRow, '', tSt('center'));   // I – navy, empty
  sc(XLSX, ws, 10, totalRow, '', tSt('center'));
  sc(XLSX, ws, 11, totalRow, '', tSt('center'));   // K – KMS Source (blank in total)

  ws['!merges'] = merges;
  ws['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: totalRow - 1, c: 10 } });
  XLSX.utils.book_append_sheet(wb, ws, id);
}

// ─── API data → report shape transformer ─────────────────────────────────────
// routeStats : raw array from getRouteStats API (getRouteDailyStats)
// userVehicles: from StoreContext  — { idx, reg, mo, ... }
// allRoutes   : from StoreContext  — { idx, nm, vids, ... }
// drivers     : from StoreContext  — { idx, nm, ... }
// filterRange : filters.range string (key into DATE_RANGES)

export const transformAPIDataToReport = (routeStats, userVehicles, allRoutes, drivers, filterRange) => {
  const vehicleMap = {};
  (userVehicles || []).forEach(v => { vehicleMap[v.idx] = v; });

  const routeNameMap = {};
  (allRoutes || []).forEach(r => { routeNameMap[r.idx] = r.nm; });

  const driverMap = {};
  // Store under both numeric and string keys to survive any type mismatch
  (drivers || []).forEach(d => {
    const name = d.nm || d.name || null;
    if (name) {
      driverMap[d.idx]         = name;
      driverMap[String(d.idx)] = name;
    }
  });

  const range       = DATE_RANGES[filterRange] || DATE_RANGES['today'];
  const dateRange   = isSameDay(range.from, range.to)
    ? stampToDate(range.from)
    : `${stampToDate(range.from)} - ${stampToDate(range.to)}`;
  const generatedOn = stampToDate(Date.now());

  // routeStats has one entry per vehicle per day — group by vid so each
  // vehicle gets exactly one sheet, with all days' trips merged together.
  const vidGroups = {};
  (routeStats || []).forEach(stat => {
    if (!vidGroups[stat.vid]) vidGroups[stat.vid] = [];
    vidGroups[stat.vid].push(stat);
  });

  const vehicles = Object.entries(vidGroups).map(([vid, dayStats]) => {
    const veh   = vehicleMap[+vid] || vehicleMap[vid] || {};
    const reg   = veh.reg  || `V-${vid}`;
    const model = veh.mo   || '-';

    const uniqueDriverIds = new Set();
    const uniqueRouteIds  = new Set();
    let totalActualKms    = 0;
    let totalEstKms       = 0;
    let totalTrips        = 0;
    const trips           = [];

    dayStats.forEach(({ routes = [] }) => {
      routes.forEach(route => {
        uniqueRouteIds.add(route.routeId);
        const routeName     = routeNameMap[route.routeId] || `Route ${route.routeId}`;
        const estKmsPerTrip = parseFloat(((route.routeDst || 0) / 1000).toFixed(2));

        (route.tripStats || []).forEach(trip => {
          const driverName = driverMap[trip.driverId] || `Driver ${trip.driverId}`;
          uniqueDriverIds.add(trip.driverId);

          const hasOdo    = trip.odoDiff != null;
          const actualKms = hasOdo
            ? parseFloat((trip.odoDiff).toFixed(2))        // odoDiff is already in km (VECV odometer)
            : parseFloat(((trip.dst || 0) / 1000).toFixed(2));
          const startTs   = trip.startTs;
          const endTs     = trip.endTs;
          const hms       = secondsToHms(trip.dur || 0, true);

          trips.push({
            date:      getLocalisedDate(startTs, true),
            driverName,
            routeName,
            startTime: getLocalisedTime(startTs, true),
            endTime:   getLocalisedTime(endTs,   true),
            duration:  `${hms.h}h ${hms.m}m`,
            actualKms,
            estKms:    estKmsPerTrip,
            kmsSource: hasOdo ? 'ODO' : 'GPS',
          });

          totalActualKms += actualKms;
          totalEstKms    += estKmsPerTrip;
          totalTrips++;
        });
      });
    });

    return {
      id:           reg,
      type:         model,
      totalRoutes:  uniqueRouteIds.size,
      totalTrips,
      totalDrivers: uniqueDriverIds.size,
      actualKms:    parseFloat(totalActualKms.toFixed(1)),
      estKms:       parseFloat(totalEstKms.toFixed(1)),
      trips,
    };
  }).filter(v => v.totalTrips > 0);

  return { dateRange, generatedOn, vehicles };
};

// ─── Public API ───────────────────────────────────────────────────────────────

export const downloadReport = async (reportData, fileName) => {
  const XLSX = await getXLSX();
  const wb   = XLSX.utils.book_new();
  buildSummary(XLSX, wb, reportData);
  reportData.vehicles.forEach(veh =>
    buildVehicleSheet(XLSX, wb, { ...veh, dateRange: reportData.dateRange })
  );
  const now = new Date();
  XLSX.writeFile(wb, fileName || `Ajjas - Vehicle Tracking Report_${reportData.dateRange || ''}_${Date.now()}.xlsx`);
};
